import React, { useEffect, useRef, useState } from 'react'
import {
  Text as RNText,
  TextStyle,
  ActivityIndicator,
  TouchableOpacity,
  ViewStyle,
} from 'react-native'

import { PersonRounded } from '@mui/icons-material'
import SentimentVeryDissatisfiedIcon from '@mui/icons-material/SentimentVeryDissatisfied'
import { InputAdornment, TextField, useMediaQuery } from '@mui/material'
import { BLOCKED_FILE_EXTENSIONS, useStores } from '@trivie/core'
import { Button, Text, color } from '@trivie/ui-mobile'
import { Avatar } from '@trivie/ui-web/components/avatar'
import { observer } from 'mobx-react-lite'
import { Controller, useForm } from 'react-hook-form'
import { OtpEntry } from '@trivie/ui-web/components/otp-entry/otp-entry'
import { useHistory } from 'react-router'
import { AlertDialog } from '@trivie/ui-web/components/alert-dialog'
import { AddPhotoAlternateRounded } from '@mui/icons-material'
import { HourlyWarning } from '@trivie/ui-web/components/hourly-warning'

export const MagicLinkSentScreen = observer(() => {
  const { userStore } = useStores()
  const { email, user, uploadProfilePhoto, setOtpNotFound, otpNotFound, evaluateOTC } = userStore

  const resend = () => userStore.sendMagicLink(email)
  const pinRef = useRef<any>(null)
  const history = useHistory()
  const [errorMessage, setErrorMessage] = useState<string | undefined>()

  const [showHourly, setShowHourly] = useState<string>('')
  const [evaluateData, setEvaluateData] = useState<any>()
  const [otp, setOtp] = useState('')

  useEffect(() => {
    setOtpNotFound(false)
  }, [])

  const { control } = useForm({ shouldUnregister: false })

  const login = () => {
    if (otp) {
      evaluateOTC(otp, email).then(async (data: any) => {
        setEvaluateData(data)
        if (userStore.status === 'error') {
          // history.replace('/magic')
        } else if (data.workspaces && data.workspaces.length > 1) {
          localStorage.removeItem('ml-error')
          if (userStore.sharedQuiz) {
            const ws = data.workspaces.find(
              (w) => w.access_code === userStore.sharedQuiz?.workspace_id,
            )
            if (ws) {
              userStore
                .loginWorkspace(data.token, userStore.sharedQuiz?.workspace_id)
                .then((loginData) => {
                  userStore.getUser(loginData.user_id, loginData.access_token).then(() => {
                    if (!userStore.user?.first_name && !userStore.user?.last_name) {
                      setShowFirstLast(true)
                    } else {
                      history.replace(`/share/${userStore.sharedQuiz?.code}`)
                    }
                  })
                })
            }
          } else {
            history.replace('/workspaces')
          }
        } else if (data.workspaces.length === 1) {
          // check show hourly alert
          localStorage.removeItem('ml-error')
          if (data.workspaces[0].hourly_warning) {
            setShowHourly(data.workspaces[0].hourly_warning)
          } else if (data.workspaces[0].has_sso_config) {
            userStore.setSSOWorkspace(data.workspaces[0])
            const ssoRedirect = await userStore?.getAccessCodeDetails(
              data.workspaces[0].access_code,
            )
            if (ssoRedirect) {
              window.location.replace(ssoRedirect)
            }
          } else {
            userStore
              .loginWorkspace(data.token, data.workspaces[0].access_code)
              .then((loginData) => {
                if (userStore?.sharedQuiz) {
                  userStore.getUser(loginData.user_id, loginData.access_token).then(() => {
                    if (!userStore.user?.first_name && !userStore.user?.last_name) {
                      setShowFirstLast(true)
                    } else {
                      history.replace(`/share/${userStore.sharedQuiz?.code}`)
                    }
                  })
                } else {
                  history.replace('/library')
                }
              })
          }
        }
      })
    }
  }

  const { updateUser } = userStore
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')

  // default to true if shared quiz
  const [showFirstLast, setShowFirstLast] = useState(
    userStore?.token && (!userStore?.user?.first_name || user?.last_name) ? true : false,
  )

  const onChange = (value: string) => {
    setOtpNotFound(false)
    setOtp(value)
  }

  const isMobile = !useMediaQuery('(min-width:800px)')

  return (
    <div style={ROOT} onClick={() => pinRef?.current?.focus()}>
      <div style={CONTAINER}>
        {showFirstLast ? (
          <div style={CONTENT}>
            <div style={HEADER}>
              <Text
                style={{ marginBottom: 12 }}
                align="center"
                tx="Onboarding.newSignup"
                variant="headline"
              />
              <Text
                variant="titleMedium"
                style={TAGLINE}
                text={
                  showFirstLast
                    ? 'Please tell us a little about yourself.'
                    : 'Sign up for a new Trivie Account'
                }
              />
            </div>
            {showFirstLast && (
              <>
                <AlertDialog
                  open={!!errorMessage}
                  title="Upload Failed"
                  message={errorMessage || ''}
                  buttonText="Close"
                  onClose={() => {
                    setErrorMessage('')
                  }}
                />
                <div style={{ position: 'relative', marginBottom: 20 }}>
                  {!userStore?.user?.photo_url && !firstName && !lastName && (
                    <AddPhotoAlternateRounded
                      htmlColor={color.black}
                      style={{
                        fontSize: 24,
                        zIndex: 999,
                        position: 'absolute',
                        top: 32,
                        left: 35,
                      }}
                    />
                  )}
                  {userStore?.isLoading ? (
                    <ActivityIndicator />
                  ) : (
                    <Controller
                      control={control}
                      render={({ value }) => (
                        <Avatar
                          size={90}
                          name={`${firstName} ${lastName}`}
                          url={value ? URL.createObjectURL(value) : user?.photo_url}
                          onChange={(file: File) => {
                            if (file) {
                              if (BLOCKED_FILE_EXTENSIONS.includes(file.type)) {
                                let targetStr = file.type
                                if (targetStr.includes('/')) {
                                  targetStr = targetStr.split('/')[1]
                                  setErrorMessage(`Sorry, but we don't support ${targetStr} images`)
                                } else {
                                  setErrorMessage(`Sorry, but we don't support ${file.type} files`)
                                }
                              } else {
                                const formData = new FormData()
                                formData.append('photo', file)
                                uploadProfilePhoto(formData, user!.id)
                              }
                            }
                          }}
                        />
                      )}
                      name="photo"
                      defaultValue=""
                    />
                  )}
                </div>
                <TextField
                  variant="standard"
                  required
                  onChange={(e: any) => setFirstName(e.target.value)}
                  style={{ width: 335, marginBottom: 51 }}
                  size="small"
                  InputProps={{
                    style: { paddingBottom: 8 },
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonRounded htmlColor={color.black} />
                      </InputAdornment>
                    ),
                  }}
                  placeholder="Your First Name"
                />
                <TextField
                  variant="standard"
                  required
                  onChange={(e: any) => setLastName(e.target.value)}
                  style={{ width: 335, marginBottom: 51 }}
                  size="small"
                  InputProps={{
                    style: { paddingBottom: 8 },
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonRounded htmlColor={color.black} />
                      </InputAdornment>
                    ),
                  }}
                  placeholder="Your Last Name"
                />
                <Button
                  accessibilityRole="button"
                  accessibilityLabel="Create Account"
                  disabled={!firstName || !lastName}
                  textStyle={{
                    color: color.palette.white,
                  }}
                  style={{
                    ...SUBMIT,
                    marginBottom: 50,
                    backgroundColor: !firstName || !lastName ? color.shade20 : color.purple,
                  }}
                  tx="Onboarding.SignUp.createAccount"
                  onPress={() => {
                    updateUser({ first_name: firstName, last_name: lastName })
                    history.push(`/share/${userStore?.sharedQuiz?.code}`)
                  }}
                ></Button>
              </>
            )}
          </div>
        ) : (
          <div style={CONTENT}>
            <div style={HEADER}>
              <Text
                style={{ marginBottom: 12 }}
                align="center"
                tx="Onboarding.Login.checkYourEmail"
                variant="headline"
              />
              <div
                style={{
                  flexDirection: isMobile ? 'column' : 'row',
                  display: 'flex',
                  paddingLeft: 12,
                  paddingRight: 12,
                  alignItems: 'center',
                }}
              >
                <Text style={TAGLINE} tx="Onboarding.Login.toLogIn" />
                <Text style={EMAIL} text={email} />
              </div>
            </div>
            <div>
              <div style={{ flexDirection: 'column', display: 'flex', paddingBottom: 75 }}>
                <Text align="center" tx="Onboarding.Login.otpLogInTextLine1" />
                <Text align="center" tx="Onboarding.Login.otpLogInTextLine2" />
              </div>
              <OtpEntry onSubmit={login} onChange={onChange} />
            </div>
            <Button
              accessibilityRole="link"
              accessibilityLabel="Continue"
              disabled={otp.length !== 4}
              textStyle={{ color: otp.length === 4 ? color.white : color.palette.grey }}
              style={{
                ...SUBMIT,
                backgroundColor: otp.length === 4 ? color.blue : 'rgba(230, 230, 230, 1)',
              }}
              tx="Common.continue"
              onPress={login}
            />
            {otpNotFound && (
              <div style={ERROR_STATE}>
                <SentimentVeryDissatisfiedIcon
                  htmlColor={color.red}
                  style={{ marginRight: 12, fontSize: 20 }}
                />
                <span style={{ fontWeight: 700, fontSize: 12, color: 'rgba(255, 0, 0, 0.8)' }}>
                  We don't recognize that code.&nbsp;
                  <span style={{ fontWeight: 500 }}>
                    Please try again, or select Resend Code below or contact your administrator.
                  </span>
                </span>
              </div>
            )}
            <Text align="center" style={NO_EMAIL}>
              {"Haven't received the email? "}
              <TouchableOpacity
                accessibilityRole="button"
                accessibilityLabel="Send Magic Link"
                onPress={resend}
              >
                <Text style={SEND_AGAIN} tx="Onboarding.Login.sendItAgain" />
              </TouchableOpacity>
              {', or email '}
              <a href={process.env.REACT_APP_SUPPORT}>
                <Text style={SUPPORT_EMAIL} text={process.env.REACT_APP_SUPPORT} />
              </a>
              {' to get in touch.'}
            </Text>
          </div>
        )}
        {showHourly && (
          <HourlyWarning
            message={showHourly}
            accept={() => {
              if (evaluateData) {
                setShowHourly('')
                userStore
                  .handleTerms('accept', evaluateData.token, evaluateData.workspaces[0].access_code)
                  .then(() => {
                    userStore
                      .loginWorkspace(evaluateData.token, evaluateData.workspaces[0].access_code)
                      .then(() => history.replace('/library'))
                  })
              }
              setShowHourly('')
            }}
            reject={() => {
              setShowHourly('')
              userStore
                .handleTerms('reject', evaluateData.token, evaluateData.workspaces[0].access_code)
                .then(() => {
                  history.replace('/login')
                })
            }}
          />
        )}
      </div>
    </div>
  )
})

const SUBMIT: ViewStyle = {
  marginHorizontal: 20,
  backgroundColor: color.palette.blue,
  alignSelf: 'center',
  width: 335,
  marginTop: 20,
  marginBottom: 28,
}
const ROOT: React.CSSProperties = {
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  width: '100%',
}
const CONTAINER: React.CSSProperties = {
  flexDirection: 'column',
  flex: 1,
  display: 'flex',
  maxHeight: 576,
  justifyContent: 'space-evenly',
  minWidth: 400,
  alignSelf: 'center',
}
const EMAIL: TextStyle = {
  textAlign: 'center',
  lineHeight: 20,
  fontWeight: '500',
  color: color.blue,
}
const SUPPORT_EMAIL: TextStyle = {
  fontWeight: '600',
  fontSize: 16,
  textDecorationLine: 'underline',
  color: color.blue,
}
const SEND_AGAIN: TextStyle = {
  fontWeight: '600',
  fontSize: 16,
  color: color.blue,
}
const NO_EMAIL: TextStyle = {
  lineHeight: 20,
  fontWeight: '400',
  color: color.black,
  fontSize: 16,
  textAlign: 'center',
  width: 350,
}
const CONTENT: React.CSSProperties = {
  height: '100%',
  flex: 1,
  backgroundColor: '#fff',
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'column',
  justifyContent: 'space-evenly',
  alignSelf: 'center',
}
const HEADER: React.CSSProperties = {
  minWidth: 319,
  flexDirection: 'column',
  display: 'flex',
}
const TAGLINE: TextStyle = {
  color: color.black,
  textAlign: 'center',
}
const ERROR_STATE: React.CSSProperties = {
  width: 349,
  height: 64,
  marginBottom: 25,
  borderRadius: 16,
  backgroundColor: 'rgba(249, 234, 234, 1)',
  paddingTop: 10,
  paddingBottom: 10,
  paddingRight: 20,
  display: 'flex',
  flexDirection: 'row',
  paddingLeft: 20,
  alignItems: 'center',
  alignSelf: 'center',
}
